import React from 'react';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';

export default function Forsikring() {
  return (
    <>
      <SEO
        title="Fastlane ApS :: Insurance"
        description="Læs om fragtforsikring og begrænset ansvar ifølge NSAB, CMR, Haag-Visby og CIM konventionerne hos Fastlane ApS."
        canonicalUrl="https://fastlane.dk/forsikring"
      />

      <PageHeader
        title="Fragtforsikring"
        subtitle="Begrænset ansvar og vigtigheden af rettidig transportforsikring."
        breadcrumbs={[{ label: 'Forsikring' }]}
      />

      <div className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8 text-slate-700 leading-relaxed">
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-[#0f2b48]">Begrænset Ansvar</h2>
            <p>
              En transportør er ikke ansvarlig for at udrede erstatning i tilfælde af skader og bortkomst af gods, hvis det skyldes:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 text-sm">
              <li>forhold som transportøren ikke kunne gøre undgå eller afværge</li>
              <li>fejl/forsømmelser fra afsender /modtager</li>
              <li>af- og pålæsning foretaget af afsender/modtager</li>
              <li>krig, strejke etc.</li>
            </ul>
          </div>

          <p>
            Typisk udføres transport under forretningsbetingelser, der begrænser transportørens og speditørens risiko og eventuelle erstatningspligt.
          </p>

          <p>
            Derfor understreger vi vigtigheden af at tegne en fragtforsikring inden uheld sker. Fragter man gods tit nok sker der uheld, idet vi har med mennesker, trafik og mekanik at gøre. Vi tegner gerne en transportforsikring for vore kunders gods hos en af vores samarbejdspartner -{' '}
            <a
              href="https://firstmarine.dk/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sky-700 hover:text-sky-900 font-semibold underline"
            >
              First Marine A/S
            </a>
            .
          </p>

          <p>
            I tvivlstilfælde er I velkommen til at bruge os til sparring. Send blot en E-mail eller ring til os.
          </p>

          {/* International Legislation Table */}
          <div className="pt-4">
            <h3 className="text-lg font-bold text-[#0f2b48] mb-3">
              Typisk er international fragt reguleret af følgende internationale lovgivning:
            </h3>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-slate-800 font-bold border-b border-slate-200">
                  <tr>
                    <th className="py-3 px-4">Konvention / Lovgivning</th>
                    <th className="py-3 px-4">Område</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr>
                    <td className="py-3 px-4 font-semibold text-[#0f2b48]">NSAB</td>
                    <td className="py-3 px-4">Spedition</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-semibold text-[#0f2b48]">CMR-Konventionen</td>
                    <td className="py-3 px-4">Vej</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-semibold text-[#0f2b48]">Warszawa-/Montreal-Konventionen</td>
                    <td className="py-3 px-4">Luft</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-semibold text-[#0f2b48]">Haag-Visby-Reglerne</td>
                    <td className="py-3 px-4">Sø</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-semibold text-[#0f2b48]">CIM-Konventionen</td>
                    <td className="py-3 px-4">Bane</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Liability Limits Table */}
          <div className="pt-4">
            <h3 className="text-lg font-bold text-[#0f2b48] mb-3">
              Alt efter hvor i transportkæden skaden er sket gælder forskelligt begrænsninger, som typisk skitseret herunder:
            </h3>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-slate-800 font-bold border-b border-slate-200">
                  <tr>
                    <th className="py-3 px-4">Transportform</th>
                    <th className="py-3 px-4">Ansvarsbegrænsning</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr>
                    <td className="py-3 px-4 font-semibold text-[#0f2b48]">Sø</td>
                    <td className="py-3 px-4">667 *SDR per collo or 2 SDR per kg gross weight</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-semibold text-[#0f2b48]">Vej</td>
                    <td className="py-3 px-4">8.33 *SDR per kg gross weight</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-semibold text-[#0f2b48]">Bane</td>
                    <td className="py-3 px-4">17 *SDR per kg gross weight</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-semibold text-[#0f2b48]">Luft</td>
                    <td className="py-3 px-4">19 *SDR per kg gross weight</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-2 italic">
              * Special Drawing Rights - blandings valuta, hvor kursen svingere med valutamarkedet.
            </p>
          </div>

          {/* Action PDF */}
          <div className="pt-4">
            <div className="p-6 bg-slate-50 rounded-xl border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h4 className="font-bold text-[#0f2b48] text-base">Fremgangsmåde ved Skade</h4>
                <p className="text-xs text-slate-600 mt-0.5">Vejledende instruktion i PDF-format om håndtering af transportskader.</p>
              </div>
              <a
                href="https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/3-3-15-rev-00-fremgangsm-de-ved-skade.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-sky-700 hover:bg-sky-800 text-white text-sm font-semibold transition-colors shrink-0"
              >
                <i className="fa-solid fa-file-pdf mr-2" aria-hidden="true" />
                <span>Hent PDF</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
